// src/components/Login/MfaGuideModal.jsx
import React from 'react';
import './MfaGuideModal.css';
import GoogleAuthIcon from '../../assets/Google_Auth.svg';
import MicrosoftAuthIcon from '../../assets/Microsoft_Authenticator_iOS_icon.png';
import AuthyIcon from '../../assets/authy.svg';
import OnePasswordIcon from '../../assets/1password.png';

const MfaGuideModal = ({ open, onClose }) => {
  if (!open) return null;

  return (
    <div className="mfa-guide-overlay" onClick={onClose}>
      <div className="mfa-guide-container" onClick={(e) => e.stopPropagation()}>
        <div className="mfa-guide-modal">
          {/* Header */}
          <div className="mfa-guide-header">
            <h2>Two Factor Authentication - Authenticator App Guide</h2>
            <button className="mfa-guide-close" onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="mfa-guide-content">
            <div className="mfa-guide-section">
              <p className="mfa-guide-intro">
                If you have access to sensitive data, having two factor authentication is a required security step to help keep information safe.
              </p>
              
              <div className="mfa-guide-note">
                <strong>Note:</strong> If you have recently lost or changed phones you will need to contact your administrator to have your two factor authentication reset before following the steps below.
              </div>
            </div>

            <div className="mfa-guide-section">
              <h3>Setting up Two Factor Authentication</h3>
              <p>If you have recently had your 2FA reset or are setting up for the first time, please follow the steps below to sync your account to your phone.</p>
              
              <ol className="mfa-guide-steps">
                <li>
                  <strong>Download the Authenticator App</strong>
                  <p>If you haven't already done so, download one of these authenticator apps:</p>
                  <ul className="mfa-guide-apps">
                    <li>
                      <div className="app-info">
                        <div>
                          <div className="app-name">
                            <img src={GoogleAuthIcon} alt="Google Authenticator" className="app-icon google-auth-icon" />
                            <strong>Google Authenticator</strong>
                          </div>
                          <span>Available on iOS and Android</span>
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="app-info">
                        <div>
                          <div className="app-name">
                            <img src={MicrosoftAuthIcon} alt="Microsoft Authenticator" className="app-icon microsoft-auth-icon" />
                            <strong>Microsoft Authenticator</strong>
                          </div>
                          <span>Available on iOS and Android</span>
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="app-info">
                        <div>
                          <div className="app-name">
                            <img src={AuthyIcon} alt="Authy" className="app-icon authy-icon" />
                            <strong>Authy</strong>
                          </div>
                          <span>Available on iOS and Android</span>
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="app-info">
                        <div>
                          <div className="app-name">
                            <img src={OnePasswordIcon} alt="1Password" className="app-icon onepassword-icon" />
                            <strong>1Password</strong>
                          </div>
                          <span>Available on iOS and Android</span>
                        </div>
                      </div>
                    </li>
                  </ul>
                </li>

                <li>
                  <strong>Add Your Account</strong>
                  <p>In your authenticator app:</p>
                  <ul>
                    <li>Look for an "Add Account" or "+" button (usually in the bottom right corner)</li>
                    <li>Select <strong>"Scan QR Code"</strong> or <strong>"Scan a barcode"</strong></li>
                  </ul>
                </li>

                <li>
                  <strong>Scan the QR Code</strong>
                  <p>Use your authenticator app to scan the QR code displayed on the login screen.</p>
                </li>

                <li>
                  <strong>Enter the Verification Code</strong>
                  <p>Once you've scanned the QR code:</p>
                  <ul>
                    <li>Your authenticator app will show a 6-digit code for RDV Systems: MFA</li>
                    <li>Enter this code in the verification field</li>
                    <li>The code changes every 30 seconds</li>
                  </ul>
                </li>

                <li>
                  <strong>Complete Setup</strong>
                  <p>Click "Sign In" to complete the two-factor authentication setup.</p>
                </li>
              </ol>
            </div>

            <div className="mfa-guide-section">
              <h3>Future Logins</h3>
              <p>After setup, you'll find your account in your authenticator app. When logging in:</p>
              <ol className="mfa-guide-steps">
                <li>Enter your email and password as usual</li>
                <li>Open your authenticator app</li>
                <li>Find your RDV Systems: MFA account entry</li>
                <li>Enter the current 6-digit code</li>
                <li>Complete your login</li>
              </ol>
            </div>

            <div className="mfa-guide-section">
              <h3>Troubleshooting</h3>
              <div className="mfa-guide-troubleshoot">
                <div className="mfa-guide-issue">
                  <h4>QR Code Won't Scan?</h4>
                  <ul>
                    <li>Make sure your camera has permission to access the camera</li>
                    <li>Try moving your phone closer or further from the screen</li>
                    <li>Ensure good lighting conditions</li>
                    <li>Clean your phone's camera lens</li>
                  </ul>
                </div>
                
                <div className="mfa-guide-issue">
                  <h4>Code Not Working?</h4>
                  <ul>
                    <li>Make sure you're using the most current code (they change every 30 seconds)</li>
                    <li>Check that your phone's time is set correctly</li>
                    <li>Try the next code if the current one expires</li>
                  </ul>
                </div>

                <div className="mfa-guide-issue">
                  <h4>Lost Your Phone?</h4>
                  <ul>
                    <li>Contact your administrator to reset your 2FA</li>
                    <li>You'll need to set up 2FA again on your new device</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MfaGuideModal;
